import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore';
import { companyService } from '../services/companyService';
import toast from 'react-hot-toast';
import { CreateCompanyData, Company } from '../types/company';
import { motion, useReducedMotion, type Variants } from 'motion/react';

import { Button, Input, Select, Card } from '../components/ui';

// Brazilian states
const BRAZILIAN_STATES = [
    { uf: 'AC', name: 'Acre' },
    { uf: 'AL', name: 'Alagoas' },
    { uf: 'AP', name: 'Amapá' },
    { uf: 'AM', name: 'Amazonas' },
    { uf: 'BA', name: 'Bahia' },
    { uf: 'CE', name: 'Ceará' },
    { uf: 'DF', name: 'Distrito Federal' },
    { uf: 'ES', name: 'Espírito Santo' },
    { uf: 'GO', name: 'Goiás' },
    { uf: 'MA', name: 'Maranhão' },
    { uf: 'MT', name: 'Mato Grosso' },
    { uf: 'MS', name: 'Mato Grosso do Sul' },
    { uf: 'MG', name: 'Minas Gerais' },
    { uf: 'PA', name: 'Pará' },
    { uf: 'PB', name: 'Paraíba' },
    { uf: 'PR', name: 'Paraná' },
    { uf: 'PE', name: 'Pernambuco' },
    { uf: 'PI', name: 'Piauí' },
    { uf: 'RJ', name: 'Rio de Janeiro' },
    { uf: 'RN', name: 'Rio Grande do Norte' },
    { uf: 'RS', name: 'Rio Grande do Sul' },
    { uf: 'RO', name: 'Rondônia' },
    { uf: 'RR', name: 'Roraima' },
    { uf: 'SC', name: 'Santa Catarina' },
    { uf: 'SP', name: 'São Paulo' },
    { uf: 'SE', name: 'Sergipe' },
    { uf: 'TO', name: 'Tocantins' },
];

const STATE_OPTIONS = [
    { value: '', label: 'UF' },
    ...BRAZILIAN_STATES.map((s) => ({ value: s.uf, label: s.uf })),
];

// Mask functions
const maskCNPJ = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 14);
    return digits
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
};

const maskCEP = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    return digits.replace(/^(\d{5})(\d)/, '$1-$2');
};

const maskPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 10) {
        return digits
            .replace(/^(\d{2})(\d)/, '($1) $2')
            .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return digits
        .replace(/^(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2');
};

// Page-load entrance variants — matches LoginPage Phase 5 pattern.
// Duration values mirror --snapfy-duration-base (300ms) and --snapfy-duration-slow (500ms).
// Easing mirrors --snapfy-ease-standard cubic-bezier(0.22,1,0.36,1).
const headingVariants: Variants = {
    hidden: { opacity: 0, y: -12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const } },
} as const;

const cardVariants: Variants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const, delay: 0.15 } },
} as const;

const RegisterCompanyPage: React.FC = () => {
    const navigate = useNavigate();
    const { user, checkAuth } = useAuthStore();
    const prefersReducedMotion = useReducedMotion();
    const [isLoading, setIsLoading] = useState(false);
    const [isFetchingCep, setIsFetchingCep] = useState(false);
    const [showFiscalData, setShowFiscalData] = useState(false);
    const [companyStatus, setCompanyStatus] = useState<'none' | 'pending' | 'rejected' | 'approved'>('none');
    const [rejectionReason, setRejectionReason] = useState<string | null>(null);
    const [existingCompanyId, setExistingCompanyId] = useState<string | null>(null);

    const [formData, setFormData] = useState<CreateCompanyData>({
        name: '',
        trade_name: '',
        document: '',
        billing_email: user?.email || '',
        phone: '',
        postal_code: '',
        address: '',
        address_number: '',
        address_complement: '',
        neighborhood: '',
        city: '',
        state: ''
    });
    const [errors, setErrors] = useState<Record<string, string[]>>({});
    const [showForm, setShowForm] = useState(false);
    const isEmailVerified = !!(user as any)?.emailVerifiedAt;

    useEffect(() => {
        const loadUserState = async () => {
            if (!user) {
                await checkAuth();
            } else if (user.companyId) {
                checkCompanyStatus();
            }
        };
        loadUserState();
    }, [user]);

    const checkCompanyStatus = async () => {
        try {
            const company = await companyService.getMyCompany() as Company;
            setExistingCompanyId(company.id);
            setCompanyStatus(company.status);

            if (company.status === 'rejected' && company.rejectionReason) {
                setRejectionReason(company.rejectionReason);
                const hasFiscalData = !!(company.document || company.postalCode || company.address);
                if (hasFiscalData) {
                    setShowFiscalData(true);
                }
                setFormData({
                    name: company.name,
                    trade_name: company.tradeName || '',
                    document: company.document || '',
                    billing_email: company.billingEmail,
                    phone: company.phone || '',
                    postal_code: company.postalCode || '',
                    address: company.address || '',
                    address_number: company.addressNumber || '',
                    address_complement: company.addressComplement || '',
                    neighborhood: company.neighborhood || '',
                    city: company.city || '',
                    state: company.state || ''
                });
            } else if (company.status === 'approved') {
                navigate('/organization');
            }
        } catch (e) {
            console.error('Failed to fetch company status');
        }
    };

    // CEP autocomplete
    const fetchAddressByCep = async (cep: string) => {
        const cleanCep = cep.replace(/\D/g, '');
        if (cleanCep.length !== 8) return;

        setIsFetchingCep(true);
        try {
            const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
            const data = await response.json();

            if (data.erro) {
                toast.error('CEP não encontrado');
                return;
            }

            setFormData(prev => ({
                ...prev,
                address: data.logradouro || prev.address,
                neighborhood: data.bairro || prev.neighborhood,
                city: data.localidade || prev.city,
                state: data.uf?.toUpperCase() || prev.state,
            }));
            toast.success('Endereço preenchido automaticamente!');
        } catch (error) {
            console.error('Failed to fetch address', error);
        } finally {
            setIsFetchingCep(false);
        }
    };

    if (companyStatus === 'pending') {
        return (
            <div className="max-w-2xl mx-auto py-16 px-4 text-center text-snapfy-text-primary">
                <Card className="p-8 border-yellow-500/30">
                    <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </div>
                    <h2 className="text-2xl font-bold mb-2">Aguardando Aprovação</h2>
                    <p className="text-snapfy-text-secondary mb-6">Recebemos o cadastro da sua empresa. Nossa equipe está analisando os dados e você receberá um email assim que o processo for concluído.</p>
                    <p className="text-sm text-snapfy-text-secondary">Enquanto isso, você pode continuar usando sua conta pessoal.</p>
                    <button
                        onClick={() => navigate('/')}
                        className="mt-6 text-snapfy-accent-soft hover:underline focus-visible:outline-none focus-visible:ring-snapfy-focus rounded"
                    >
                        Voltar para Home
                    </button>
                </Card>
            </div>
        );
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        let maskedValue = value;

        // Apply masks
        if (name === 'document') {
            maskedValue = maskCNPJ(value);
        } else if (name === 'postal_code') {
            maskedValue = maskCEP(value);
        } else if (name === 'phone') {
            maskedValue = maskPhone(value);
        }

        setFormData(prev => ({ ...prev, [name]: maskedValue }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: [] }));
        }
    };

    const handleCepBlur = () => {
        const cleanCep = formData.postal_code.replace(/\D/g, '');
        if (cleanCep.length === 8) {
            fetchAddressByCep(cleanCep);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            if (companyStatus === 'rejected' && existingCompanyId) {
                await companyService.update(existingCompanyId, formData);
                toast.success('Solicitação reenviada! Aguardando nova análise.');
                setCompanyStatus('pending');
                window.scrollTo(0, 0);
            } else {
                await companyService.create(formData);
                toast.success('Empresa cadastrada com sucesso! Aguardando aprovação.');
                setCompanyStatus('pending');
                await checkAuth();
            }
        } catch (error: any) {
            console.error(error);

            if (error.response?.data?.message?.includes('User already belongs') || error.response?.data?.message?.includes('usuário já pertence')) {
                toast('Verificando cadastro existente...', { icon: '🔄' });
                await checkAuth();
                await checkCompanyStatus();
                return;
            }

            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
                const firstError = Object.values(error.response.data.errors)[0] as string[];
                toast.error(firstError?.[0] || 'Verifique os erros no formulário.');
            } else {
                toast.error(error.response?.data?.message || 'Erro ao salvar dados.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Show landing if no existing company and form not started
    if (companyStatus === 'none' && !showForm) {
        return (
            <div className="max-w-4xl mx-auto py-10 px-4 sm:px-6 lg:px-8 text-snapfy-text-primary">
                {/* Hero — page-load entrance (one per page, useReducedMotion-guarded) */}
                <motion.div
                    className="text-center mb-10"
                    variants={prefersReducedMotion
                        ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } }
                        : headingVariants}
                    initial="hidden"
                    animate="visible"
                >
                    <div className="inline-block bg-green-500/20 text-green-400 text-sm font-medium px-4 py-1 rounded-full mb-4">
                        Comece grátis com 20 créditos
                    </div>
                    <h1 className="text-4xl font-extrabold mb-4">
                        Leve sua equipe para o <span className="text-snapfy-accent-soft">próximo nível</span>
                    </h1>
                    <p className="text-snapfy-text-secondary max-w-2xl mx-auto">
                        Centralize seus designers no Snapfy para moda e decor. Créditos compartilhados, galeria unificada e controle total de consumo.
                    </p>
                </motion.div>

                {/* Benefits grid */}
                <motion.div
                    variants={prefersReducedMotion
                        ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } }
                        : cardVariants}
                    initial="hidden"
                    animate="visible"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
                        <Card className="p-6">
                            <div className="text-snapfy-accent-soft text-2xl mb-3">👥</div>
                            <h3 className="font-bold text-lg mb-2">Equipe Compartilhada</h3>
                            <p className="text-snapfy-text-secondary text-sm">Adicione designers e operadores. Todos compartilham o mesmo pool de créditos.</p>
                        </Card>
                        <Card className="p-6">
                            <div className="text-snapfy-accent-soft text-2xl mb-3">🖼️</div>
                            <h3 className="font-bold text-lg mb-2">Galeria da Empresa</h3>
                            <p className="text-snapfy-text-secondary text-sm">Todos os membros veem as imagens geradas pela equipe em uma galeria compartilhada.</p>
                        </Card>
                        <Card className="p-6">
                            <div className="text-snapfy-accent-soft text-2xl mb-3">📊</div>
                            <h3 className="font-bold text-lg mb-2">Relatórios de Consumo</h3>
                            <p className="text-snapfy-text-secondary text-sm">Acompanhe quanto cada membro consome. Relatórios detalhados por período.</p>
                        </Card>
                        <Card className="p-6">
                            <div className="text-snapfy-accent-soft text-2xl mb-3">💼</div>
                            <h3 className="font-bold text-lg mb-2">Planos Empresariais</h3>
                            <p className="text-snapfy-text-secondary text-sm">Planos com mais créditos e funcionalidades exclusivas para times.</p>
                        </Card>
                    </div>

                    {/* Free plan callout — border-snapfy-primary/30 replaces border-[#7063f7]/30 */}
                    <Card className="p-6 border-snapfy-primary/30 mb-8">
                        <div className="flex items-center gap-3 mb-3">
                            <h3 className="font-bold text-lg">Plano Free Business</h3>
                            <span className="bg-green-500/20 text-green-400 text-xs font-bold px-2 py-0.5 rounded-full">GRÁTIS</span>
                        </div>
                        <div className="flex gap-6 text-sm text-snapfy-text-secondary">
                            <span>20 créditos para começar</span>
                            <span>Até 5 membros</span>
                            <span>Galeria compartilhada</span>
                        </div>
                    </Card>

                    {/* CTA */}
                    {!isEmailVerified ? (
                        <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-card p-6 text-center">
                            <p className="text-yellow-300 mb-3">Para criar uma empresa, você precisa verificar seu email primeiro.</p>
                            <button
                                onClick={() => navigate('/verify-email')}
                                className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-3 px-8 rounded-lg focus-visible:outline-none focus-visible:ring-snapfy-focus"
                            >
                                Verificar Email
                            </button>
                        </div>
                    ) : (
                        <div className="text-center">
                            <Button
                                variant="solid"
                                onClick={() => setShowForm(true)}
                                className="text-lg py-3 px-10"
                            >
                                Cadastrar Empresa
                            </Button>
                            <p className="text-snapfy-text-secondary text-sm mt-3">Como funciona? Preencha os dados → nossa equipe aprova → comece a usar!</p>
                        </div>
                    )}
                </motion.div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto py-10 px-4 sm:px-6 lg:px-8 text-snapfy-text-primary">
            {/* Header — page-load entrance (one per page, useReducedMotion-guarded) */}
            <motion.div
                className="flex items-center gap-3 mb-8"
                variants={prefersReducedMotion
                    ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } }
                    : headingVariants}
                initial="hidden"
                animate="visible"
            >
                {companyStatus === 'none' && (
                    <button
                        onClick={() => setShowForm(false)}
                        className="text-snapfy-text-secondary hover:text-snapfy-text-primary focus-visible:outline-none focus-visible:ring-snapfy-focus rounded"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                )}
                <h1 className="text-3xl font-extrabold">Cadastro de Empresa</h1>
            </motion.div>

            <motion.form
                onSubmit={handleSubmit}
                className="bg-snapfy-surface shadow-card rounded-card p-6 border border-snapfy-border space-y-6"
                variants={prefersReducedMotion
                    ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } }
                    : cardVariants}
                initial="hidden"
                animate="visible"
            >

                {/* Rejection Reason Alert */}
                {companyStatus === 'rejected' && rejectionReason && (
                    <div className="p-4 bg-snapfy-error/10 border border-snapfy-error/30 rounded-card">
                        <div className="flex items-start space-x-3">
                            <svg className="w-6 h-6 text-snapfy-error flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <div>
                                <h3 className="text-snapfy-error font-semibold">Cadastro Rejeitado</h3>
                                <p className="text-snapfy-error/80 mt-1">{rejectionReason}</p>
                                <p className="text-snapfy-text-secondary text-sm mt-2">
                                    Por favor, corrija os dados informados acima e reenvie seu cadastro.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                    <div className="col-span-2">
                        <Input
                            type="text"
                            name="name"
                            label="Razão Social *"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            error={errors.name?.[0]}
                        />
                    </div>

                    <div className="col-span-1">
                        <Input
                            type="text"
                            name="trade_name"
                            label="Nome Fantasia"
                            value={formData.trade_name}
                            onChange={handleChange}
                            error={errors.trade_name?.[0]}
                        />
                    </div>

                    <div className="col-span-1">
                        <Input
                            type="email"
                            name="billing_email"
                            label="Email Financeiro"
                            value={formData.billing_email}
                            onChange={handleChange}
                            placeholder={user?.email || ''}
                            error={errors.billing_email?.[0]}
                        />
                        <p className="mt-1 text-xs text-snapfy-text-secondary">Deixe em branco para usar o email da sua conta</p>
                    </div>

                    <div className="col-span-1">
                        <Input
                            type="text"
                            name="phone"
                            label="Telefone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="(00) 00000-0000"
                        />
                    </div>
                </div>

                {/* Collapsible Fiscal Data Section */}
                <div className="border-t border-snapfy-border pt-6">
                    <button
                        type="button"
                        onClick={() => setShowFiscalData(!showFiscalData)}
                        className="flex items-center gap-2 text-snapfy-accent-soft hover:text-snapfy-accent-soft transition-colors text-sm font-medium focus-visible:outline-none focus-visible:ring-snapfy-focus rounded"
                    >
                        <svg
                            className={`w-4 h-4 transition-transform ${showFiscalData ? 'rotate-90' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        Adicionar Dados Fiscais para NF (opcional)
                    </button>

                    {showFiscalData && (
                        <div className="mt-4 space-y-6">
                            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                                <div className="col-span-1">
                                    <Input
                                        type="text"
                                        name="document"
                                        label="CNPJ"
                                        value={formData.document}
                                        onChange={handleChange}
                                        placeholder="00.000.000/0000-00"
                                        error={errors.document?.[0]}
                                    />
                                </div>
                            </div>

                            <div>
                                <h4 className="text-md font-medium text-snapfy-text-primary mb-4">Endereço</h4>
                                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                    <div className="sm:col-span-2">
                                        {/* CEP field — native input with onBlur handler preserved;
                                            Input primitive does not expose onBlur via its interface
                                            so we pass it through rest props (...rest in InputProps extends
                                            Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>).
                                            isFetchingCep spinner uses a trailing affordance. */}
                                        <div className="relative">
                                            <Input
                                                type="text"
                                                name="postal_code"
                                                label="CEP"
                                                value={formData.postal_code}
                                                onChange={handleChange}
                                                onBlur={handleCepBlur}
                                                placeholder="00000-000"
                                                error={errors.postal_code?.[0]}
                                                trailingAffordance={
                                                    isFetchingCep ? (
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-snapfy-primary" />
                                                    ) : undefined
                                                }
                                            />
                                        </div>
                                    </div>

                                    <div className="sm:col-span-4">
                                        <Input
                                            type="text"
                                            name="address"
                                            label="Logradouro"
                                            value={formData.address}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="sm:col-span-2">
                                        <Input
                                            type="text"
                                            name="address_number"
                                            label="Número"
                                            value={formData.address_number}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="sm:col-span-4">
                                        <Input
                                            type="text"
                                            name="address_complement"
                                            label="Complemento"
                                            value={formData.address_complement}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="sm:col-span-2">
                                        <Input
                                            type="text"
                                            name="neighborhood"
                                            label="Bairro"
                                            value={formData.neighborhood}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="sm:col-span-3">
                                        <Input
                                            type="text"
                                            name="city"
                                            label="Cidade"
                                            value={formData.city}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="sm:col-span-1">
                                        {/* State dropdown — Select primitive; onChange fires (value: string) */}
                                        <label className="block text-sm font-medium text-snapfy-text-secondary mb-1.5">Estado</label>
                                        <Select
                                            options={STATE_OPTIONS}
                                            value={formData.state}
                                            onChange={(value) =>
                                                setFormData(prev => ({ ...prev, state: value }))
                                            }
                                            placeholder="UF"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex justify-end pt-5">
                    <Button
                        type="submit"
                        variant="solid"
                        isLoading={isLoading}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Cadastrando...' : 'Finalizar Cadastro'}
                    </Button>
                </div>
            </motion.form>
        </div>
    );
};

export default RegisterCompanyPage;
